/**
 * My Leaves Component
 * Shows user's leave history and status
 */

import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { 
  Calendar, 
  Clock, 
  CheckCircle, 
  XCircle,
  AlertCircle,
  ChevronLeft,
  ChevronRight,
  Filter,
  Ban,
  Loader2,
  Undo2,
  FileText,
  Hourglass,
  MinusCircle,
  CalendarDays,
  CircleSlash
} from 'lucide-react';
import { formatCalendarDate, formatDateOnly } from '../../utils/formatters';
import { useCountUp } from '../shared/Motion';

const API = process.env.REACT_APP_BACKEND_URL || '';

const STATUS_CONFIG = {
  pending: { 
    label: 'Pending', 
    color: 'yellow', 
    icon: Clock,
    bgColor: 'bg-yellow-100',
    textColor: 'text-yellow-800',
    borderColor: 'border-yellow-200'
  },
  pending_hr: {
    label: 'Pending HR Approval',
    color: 'orange',
    icon: Clock,
    bgColor: 'bg-orange-100',
    textColor: 'text-orange-800',
    borderColor: 'border-orange-200'
  },
  approved: { 
    label: 'Approved', 
    color: 'green', 
    icon: CheckCircle,
    bgColor: 'bg-green-100',
    textColor: 'text-green-800',
    borderColor: 'border-green-200'
  },
  rejected: { 
    label: 'Rejected', 
    color: 'red', 
    icon: XCircle,
    bgColor: 'bg-red-100',
    textColor: 'text-red-800',
    borderColor: 'border-red-200'
  },
  cancelled: {
    label: 'Cancelled',
    color: 'gray',
    icon: Ban,
    bgColor: 'bg-gray-200',
    textColor: 'text-gray-700',
    borderColor: 'border-gray-200'
  }
};

const LEAVE_TYPE_LABELS = {
  casual: 'Casual',
  sick: 'Sick',
  planned: 'Planned',
  emergency: 'Emergency'
};

const MyLeaves = () => {
  const [leaves, setLeaves] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [statusFilter, setStatusFilter] = useState('');
  const [cancelTarget, setCancelTarget] = useState(null);
  const [cancelReason, setCancelReason] = useState('');
  const [cancelling, setCancelling] = useState(false);
  const [cancelError, setCancelError] = useState(null);
  const [cancelSuccess, setCancelSuccess] = useState(null);
  const [counts, setCounts] = useState(null);
  const limit = 10;

  // MYLEAVES-UI-001: status counts from the same API (limit=1 per status → exact `total`), never hardcoded.
  const fetchCounts = useCallback(async () => {
    try {
      const keys = ['', 'pending', 'approved', 'rejected', 'cancelled'];
      const res = await Promise.all(keys.map(k => axios.get(`${API}/api/leave/my-requests`, { params: { page: 1, limit: 1, ...(k ? { status: k } : {}) } })));
      setCounts({ total: res[0].data.total || 0, pending: res[1].data.total || 0, approved: res[2].data.total || 0, rejected: res[3].data.total || 0, cancelled: res[4].data.total || 0 });
    } catch (err) {
      console.error('Error fetching leave counts:', err);
    }
  }, []);
  useEffect(() => { fetchCounts(); }, [fetchCounts]);

  const fetchLeaves = useCallback(async () => {
    try {
      setLoading(true);
      const params = { page, limit };
      if (statusFilter) params.status = statusFilter;
      
      const response = await axios.get(`${API}/api/leave/my-requests`, { params });
      setLeaves(response.data.requests || []);
      setTotal(response.data.total || 0);
      setError(null);
    } catch (err) {
      console.error('Error fetching leaves:', err);
      setError('Failed to load leave requests');
    } finally {
      setLoading(false);
    }
  }, [page, statusFilter]);

  useEffect(() => {
    fetchLeaves();
  }, [fetchLeaves]);

  const formatDate = (dateStr) => {
    if (!dateStr) return '--';
    return formatDateOnly(dateStr);
  };

  const totalPages = Math.ceil(total / limit);

  const now = new Date();
  const todayIso = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
  const canRequestCancellation = (leave) =>
    leave.status === 'approved' && (leave.start_date || '') > todayIso && leave.cancellation_status !== 'requested';
  const canWithdraw = (leave) => ['pending', 'pending_hr'].includes(leave.status);

  const openCancelModal = (leave) => {
    setCancelTarget(leave);
    setCancelReason('');
    setCancelError(null);
  };

  const handleConfirmCancel = async () => {
    if (!cancelTarget) return;
    const isApproved = cancelTarget.status === 'approved';
    try {
      setCancelling(true);
      setCancelError(null);
      const endpoint = isApproved
        ? `${API}/api/leave/${cancelTarget.id}/request-cancellation`
        : `${API}/api/leave/${cancelTarget.id}/cancel`;
      const res = await axios.post(endpoint, { reason: cancelReason.trim() || null });
      setCancelTarget(null);
      setCancelSuccess(res.data?.message || (isApproved ? 'Cancellation request submitted.' : 'Leave withdrawn successfully.'));
      setTimeout(() => setCancelSuccess(null), 8000);
      fetchLeaves();
      fetchCounts();
    } catch (err) {
      setCancelError(err.response?.data?.detail || 'Request failed. Please try again.');
    } finally {
      setCancelling(false);
    }
  };

  const statusMeta = (leave) => {
    if (leave.status === 'approved' && leave.cancellation_status === 'requested') return { key: 'pending', title: 'Cancellation Pending', hint: 'Awaiting your Team Leader\'s decision.', Icon: Clock };
    switch (leave.status) {
      case 'approved': return { key: 'approved', title: 'Approved', hint: 'Enjoy your time off!', Icon: CheckCircle };
      case 'rejected': return { key: 'rejected', title: 'Rejected', hint: 'Unfortunately, this request was not approved.', Icon: XCircle };
      case 'cancelled': return { key: 'cancelled', title: 'Cancelled', hint: 'This request was cancelled.', Icon: MinusCircle };
      case 'pending_hr': return { key: 'pending', title: 'Awaiting HR Approval', hint: 'We\'ll notify you once there\'s an update.', Icon: Clock };
      default: return { key: 'pending', title: 'Awaiting Approval', hint: 'We\'ll notify you once there\'s an update.', Icon: Clock };
    }
  };
  const TONE = {
    pending: { card: 'border-amber-200/80 bg-gradient-to-r from-amber-50/40 to-white', date: 'bg-amber-50 text-amber-700 border-amber-100', pill: 'bg-amber-100 text-amber-800', panel: 'bg-amber-50/80 border-amber-100 text-amber-900', icon: 'text-amber-500', hint: 'text-amber-700/80' },
    approved: { card: 'border-emerald-200/80 bg-gradient-to-r from-emerald-50/40 to-white', date: 'bg-emerald-50 text-emerald-700 border-emerald-100', pill: 'bg-emerald-100 text-emerald-800', panel: 'bg-emerald-50/80 border-emerald-100 text-emerald-900', icon: 'text-emerald-600', hint: 'text-emerald-700/80' },
    rejected: { card: 'border-rose-200/80 bg-gradient-to-r from-rose-50/50 to-white', date: 'bg-rose-50 text-rose-700 border-rose-100', pill: 'bg-rose-100 text-rose-800', panel: 'bg-rose-50/80 border-rose-100 text-rose-900', icon: 'text-rose-600', hint: 'text-rose-700/80' },
    cancelled: { card: 'border-slate-200 bg-gradient-to-r from-slate-50/60 to-white', date: 'bg-slate-100 text-slate-600 border-slate-200', pill: 'bg-slate-200 text-slate-700', panel: 'bg-slate-50 border-slate-200 text-slate-800', icon: 'text-slate-500', hint: 'text-slate-500' },
  };
  const typeLabel = (t) => `${LEAVE_TYPE_LABELS[t] || (t || '').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())} Leave`.replace('Unpaid Leave', 'Unpaid Leave');
  const monthAbbr = (d) => (d ? new Date(d + 'T00:00:00').toLocaleString('en-US', { month: 'short' }).toUpperCase() : '');
  const dayNum = (d) => (d ? String(new Date(d + 'T00:00:00').getDate()).padStart(2, '0') : '--');
  const yearNum = (d) => (d ? new Date(d + 'T00:00:00').getFullYear() : '');

  const SUMMARY = [
    { key: '', label: 'Total Requests', Icon: FileText, tone: 'from-white to-indigo-50/60', iconCls: 'bg-indigo-100 text-indigo-600', num: 'text-slate-900' },
    { key: 'pending', label: 'Pending', Icon: Hourglass, tone: 'from-white to-amber-50/70', iconCls: 'bg-amber-100 text-amber-600', num: 'text-amber-600' },
    { key: 'approved', label: 'Approved', Icon: CheckCircle, tone: 'from-white to-emerald-50/70', iconCls: 'bg-emerald-100 text-emerald-600', num: 'text-emerald-600' },
    { key: 'rejected', label: 'Rejected', Icon: XCircle, tone: 'from-white to-rose-50/70', iconCls: 'bg-rose-100 text-rose-600', num: 'text-rose-600' },
    { key: 'cancelled', label: 'Cancelled', Icon: CircleSlash, tone: 'from-white to-slate-50', iconCls: 'bg-slate-200 text-slate-600', num: 'text-slate-700' },
  ];
  const SummaryCard = ({ item, value, active, onClick, delay }) => {
    const n = useCountUp(value ?? 0, 700);
    return (
      <button type="button" onClick={onClick} aria-pressed={active} data-testid={`my-leaves-summary-${item.key || 'total'}`}
        className={`lv-card lv-lift an-rise text-left flex items-center gap-3 px-3.5 py-2.5 bg-gradient-to-br ${item.tone} ${active ? 'ring-2 ring-violet-400/70 border-violet-300' : ''}`} style={{ animationDelay: `${delay}ms` }}>
        <span className={`h-10 w-10 shrink-0 rounded-xl flex items-center justify-center ${item.iconCls}`}><item.Icon className="h-4.5 w-4.5" strokeWidth={2} /></span>
        <span className="min-w-0"><span className={`block text-[22px] font-extrabold leading-none tabular-nums ${item.num}`} data-testid={`my-leaves-summary-${item.key || 'total'}-value`}>{value == null ? '—' : n}</span><span className="block text-[12px] text-slate-500 mt-0.5 truncate">{item.label}</span></span>
      </button>
    );
  };

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-xl p-4" data-testid="my-leaves-error">
        <div className="flex items-center gap-2 text-red-700"><AlertCircle className="w-5 h-5" /><span>{error}</span></div>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="my-leaves-root">
      {cancelSuccess && (
        <div className="an-pop flex items-center gap-2 bg-emerald-50 border border-emerald-200 text-emerald-800 rounded-xl px-4 py-3 text-sm" data-testid="cancel-success-banner">
          <CheckCircle className="w-4 h-4 flex-shrink-0" />
          {cancelSuccess}
        </div>
      )}

      {/* Toolbar — title + status filter only (Apply Leave lives in the top navigation) */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h3 className="text-[20px] font-bold text-slate-900 tracking-tight leading-tight">My Leave Requests</h3>
          <p className="text-[12.5px] text-slate-500 mt-0.5">Track the status of your leave applications.</p>
        </div>
        <div className="flex items-center gap-2 shrink-0" data-testid="my-leaves-toolbar">
          <Filter className="w-4 h-4 text-slate-500" />
          <select
            value={statusFilter}
            data-testid="leave-status-filter"
            onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
            className="lv-input h-9 w-[140px] rounded-xl border border-slate-200 bg-white px-3 text-[13px] font-medium text-slate-700"
          >
            <option value="">All Status</option>
            <option value="pending">Pending</option>
            <option value="approved">Approved</option>
            <option value="rejected">Rejected</option>
            <option value="cancelled">Cancelled</option>
          </select>
        </div>
      </div>

      {/* Summary — live counts; click = status filter */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3" data-testid="my-leaves-summary">
        {SUMMARY.map((it, i) => (
          <SummaryCard key={it.key || 'total'} item={it} value={counts ? counts[it.key || 'total'] : null} active={!!it.key && statusFilter === it.key}
            onClick={() => { setStatusFilter(it.key); setPage(1); }} delay={60 + i * 50} />
        ))}
      </div>

      {/* Request cards */}
      {loading && leaves.length === 0 ? (
        <div className="space-y-3" data-testid="my-leaves-loading">
          {[1, 2, 3].map(i => <div key={i} className="lv-card p-4 animate-pulse h-[96px]" />)}
        </div>
      ) : leaves.length === 0 ? (
        <div className="lv-card an-rise text-center py-12" data-testid="my-leaves-empty">
          <span className="mx-auto mb-3 h-12 w-12 rounded-full bg-slate-100 text-slate-400 flex items-center justify-center"><Calendar className="w-6 h-6" /></span>
          <p className="text-slate-600 font-medium">No leave requests found</p>
          {statusFilter && <button type="button" onClick={() => { setStatusFilter(''); setPage(1); }} className="mt-2 text-[13px] font-semibold text-blue-600 hover:text-blue-800" data-testid="my-leaves-clear-filter">Show all requests</button>}
        </div>
      ) : (
        <div key={`${statusFilter}-${page}`} className={`space-y-2.5 transition-opacity ${loading ? 'opacity-60' : ''}`} data-testid="my-leaves-list">
          {leaves.map((leave, idx) => {
            const meta = statusMeta(leave); const t = TONE[meta.key]; const StatusIcon = meta.Icon;
            const statusCfg = STATUS_CONFIG[leave.status] || STATUS_CONFIG.pending; const PillIcon = statusCfg.icon;
            const statusText = leave.status === 'cancelled' && leave.cancelled_stage === 'employee_approved' ? 'Cancelled by Employee – Approved'
              : leave.status === 'cancelled' && leave.cancelled_stage === 'employee' ? 'Cancelled by Employee' : statusCfg.label;
            return (
              <div key={leave.id} data-testid={`leave-card-${leave.id}`} className={`lv-card lv-lift an-rise border p-3 ${t.card}`} style={{ animationDelay: `${Math.min(idx, 6) * 45}ms` }}>
                <div className="grid grid-cols-[64px_minmax(0,1fr)] lg:grid-cols-[64px_minmax(0,1fr)_minmax(260px,320px)_auto] gap-3 lg:gap-4 items-center">
                  {/* Date block */}
                  <div className={`h-[78px] w-16 rounded-xl border flex flex-col items-center justify-center leading-none ${t.date}`}>
                    <span className="text-[10px] font-bold tracking-wide">{monthAbbr(leave.start_date)}</span>
                    <span className="text-[24px] font-extrabold mt-0.5">{dayNum(leave.start_date)}</span>
                    <span className="text-[10.5px] font-medium opacity-80 mt-0.5">{yearNum(leave.start_date)}</span>
                  </div>
                  {/* Details */}
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-1.5">
                      <span data-testid={`leave-status-${leave.id}`} className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold ${t.pill}`}><PillIcon className="w-3 h-3" />{statusText}</span>
                      {leave.status === 'approved' && leave.cancellation_status === 'requested' && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold bg-amber-100 text-amber-800" data-testid={`cancellation-pending-badge-${leave.id}`}><Clock className="w-3 h-3" />Cancellation Pending</span>
                      )}
                      <span className="px-2 py-0.5 rounded-full text-[11px] font-semibold bg-slate-100 text-slate-700">{typeLabel(leave.leave_type)}</span>
                    </div>
                    <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[13px] text-slate-700 mt-1.5">
                      <CalendarDays className="w-3.5 h-3.5 text-slate-500" />
                      <span className="font-medium">{formatCalendarDate(leave.start_date)} — {formatCalendarDate(leave.end_date)}</span>
                      <span className="text-slate-300">•</span>
                      <span className="font-semibold">{leave.total_days} day(s)</span>
                      {Number(leave.sandwich_bridge_days) > 0 && <span className="inline-flex items-center rounded-full bg-amber-100 text-amber-800 px-1.5 py-0.5 text-[10.5px] font-semibold" title={`Includes ${leave.sandwich_bridge_days} intervening holiday/weekly-off day(s) between this and your adjacent leave (sandwich policy)`} data-testid={`my-leave-sandwich-${leave.id}`}>incl. {leave.sandwich_bridge_days} sandwich{leave.sandwich_bridge_status === 'provisional' ? ' · provisional' : ''}</span>}
                    </div>
                    <p className="text-[13px] text-slate-600 line-clamp-1 mt-0.5" title={leave.reason}>{leave.reason}</p>
                    <p className="text-[11.5px] text-slate-400 mt-0.5">Applied on {formatDate(leave.created_at)}</p>

                    {leave.approval_comments && (
                      <div className="mt-2 pt-2 border-t border-slate-100">
                        <p className="text-[11.5px] text-slate-500"><strong>{leave.status === 'approved' ? 'Approved' : 'Rejected'} by:</strong> {leave.approved_by_name || 'N/A'}</p>
                        <p className="text-[12.5px] text-slate-600 italic">&ldquo;{leave.approval_comments}&rdquo;</p>
                      </div>
                    )}
                    {leave.status === 'cancelled' && (
                      <div className="mt-2 pt-2 border-t border-slate-100" data-testid="cancellation-info">
                        <p className="text-[11.5px] text-slate-500"><strong>Cancelled by:</strong> {leave.cancelled_by_name || 'N/A'}{leave.cancelled_at && ` on ${formatDate(leave.cancelled_at)}`}</p>
                        {leave.cancelled_stage === 'employee_approved' && leave.cancellation_decided_by_name && <p className="text-[11.5px] text-slate-500">Cancellation approved by {leave.cancellation_decided_by_name}</p>}
                        {leave.cancellation_reason && <p className="text-[12.5px] text-slate-600 italic">&ldquo;{leave.cancellation_reason}&rdquo;</p>}
                        {Number(leave.restored_days) > 0 && <p className="text-[11.5px] text-emerald-700 mt-0.5 flex items-center gap-1"><Undo2 className="w-3 h-3" /> {leave.restored_days} day(s) restored to balance</p>}
                      </div>
                    )}
                    {leave.status === 'approved' && leave.cancellation_status === 'requested' && (
                      <div className="mt-2 pt-2 border-t border-amber-100" data-testid="cancellation-requested-info">
                        <p className="text-[11.5px] text-amber-800">Cancellation requested{leave.cancellation_requested_at && ` on ${formatDate(leave.cancellation_requested_at)}`} — awaiting Team Leader approval. Your leave and balance stay unchanged until it is approved.</p>
                        {leave.cancellation_reason && <p className="text-[12.5px] text-amber-700 italic">&ldquo;{leave.cancellation_reason}&rdquo;</p>}
                      </div>
                    )}
                    {leave.status === 'approved' && leave.cancellation_status === 'rejected' && (
                      <div className="mt-2 pt-2 border-t border-red-100" data-testid="cancellation-rejected-info">
                        <p className="text-[11.5px] text-red-700 font-semibold">Cancellation request rejected by {leave.cancellation_decided_by_name || 'your Team Leader'} — the leave remains approved.</p>
                        {leave.cancellation_rejection_reason && <p className="text-[12.5px] text-red-800 italic">&ldquo;{leave.cancellation_rejection_reason}&rdquo;</p>}
                      </div>
                    )}
                    {leave.rejection_reason && (
                      <div className="mt-2 pt-2 border-t border-red-100" data-testid="rejection-reason-display">
                        <p className="text-[11.5px] font-semibold text-red-700">Rejected by {leave.approved_by_name || leave.rejected_by_name || 'Manager'}</p>
                        <p className="text-[12.5px] text-red-800">{leave.rejection_reason}</p>
                      </div>
                    )}
                  </div>
                  {/* Status panel */}
                  <div className={`col-span-2 lg:col-span-1 rounded-xl border px-3.5 py-2.5 flex items-center gap-3 ${t.panel}`} data-testid={`leave-status-panel-${leave.id}`}>
                    <StatusIcon className={`w-5 h-5 shrink-0 ${t.icon}`} />
                    <div className="min-w-0"><p className="text-[13.5px] font-bold leading-tight">{meta.title}</p><p className={`text-[11.5px] leading-tight mt-0.5 ${t.hint}`}>{meta.hint}</p></div>
                  </div>
                  {/* Action */}
                  <div className="col-span-2 lg:col-span-1 flex lg:justify-end lg:min-w-[172px]">
                    {canRequestCancellation(leave) && (
                      <button onClick={() => openCancelModal(leave)} data-testid={`cancel-leave-btn-${leave.id}`}
                        className="an-btn inline-flex items-center gap-1.5 h-9 px-3.5 rounded-xl border border-slate-200 bg-white text-[12.5px] font-semibold text-slate-700 hover:bg-slate-50 hover:border-slate-300">
                        <Ban className="w-3.5 h-3.5" /> Request Cancellation
                      </button>
                    )}
                    {canWithdraw(leave) && (
                      <button onClick={() => openCancelModal(leave)} data-testid={`withdraw-leave-btn-${leave.id}`}
                        className="an-btn inline-flex items-center gap-1.5 h-9 px-3.5 rounded-xl border border-amber-200 bg-white text-[12.5px] font-semibold text-amber-700 hover:bg-amber-50">
                        <Undo2 className="w-3.5 h-3.5" /> Withdraw Request
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between pt-1" data-testid="my-leaves-pagination">
          <p className="text-[12.5px] text-slate-500">Showing {((page - 1) * limit) + 1} - {Math.min(page * limit, total)} of {total}</p>
          <div className="flex items-center gap-2">
            <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} className="an-btn h-8 w-8 rounded-lg border border-slate-200 bg-white disabled:opacity-50 disabled:cursor-not-allowed hover:bg-slate-50 flex items-center justify-center" data-testid="my-leaves-prev"><ChevronLeft className="w-4 h-4" /></button>
            <span className="text-[12.5px] text-slate-600">Page {page} of {totalPages}</span>
            <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages} className="an-btn h-8 w-8 rounded-lg border border-slate-200 bg-white disabled:opacity-50 disabled:cursor-not-allowed hover:bg-slate-50 flex items-center justify-center" data-testid="my-leaves-next"><ChevronRight className="w-4 h-4" /></button>
          </div>
        </div>
      )}

      {/* Cancel confirmation modal (logic unchanged) */}
      {cancelTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 backdrop-blur-[2px] p-4" data-testid="cancel-leave-modal">
          <div className="an-pop bg-white rounded-2xl shadow-[0_24px_60px_-20px_rgba(15,23,42,.45)] w-full max-w-md p-6">
            <div className="flex items-start gap-3 mb-4">
              <div className="p-2 bg-red-100 rounded-full flex-shrink-0"><AlertCircle className="w-5 h-5 text-red-600" /></div>
              <div>
                <h4 className="text-base font-semibold text-gray-900">{cancelTarget.status === 'approved' ? 'Request cancellation of this approved leave?' : 'Withdraw this pending request?'}</h4>
                <p className="text-sm text-gray-600 mt-1">
                  {cancelTarget.status === 'approved'
                    ? 'Your Team Leader must approve this cancellation. The leave stays active and your balance stays unchanged until it is approved.'
                    : 'Are you sure you want to withdraw this leave request before approval?'}
                </p>
                <p className="text-sm text-gray-700 mt-2 bg-gray-50 rounded-lg px-3 py-2">
                  {formatCalendarDate(cancelTarget.start_date)} — {formatCalendarDate(cancelTarget.end_date)}<span className="text-gray-400"> • </span><span className="font-medium">{cancelTarget.total_days} day(s)</span>
                </p>
                {cancelTarget.status === 'approved' && <p className="text-xs text-gray-500 mt-2">The {cancelTarget.total_days} charged day(s) will be restored only after your Team Leader approves.</p>}
              </div>
            </div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Cancellation reason (optional)</label>
            <textarea value={cancelReason} onChange={(e) => setCancelReason(e.target.value)} rows={2} maxLength={500} placeholder="e.g., Plans changed, leave no longer needed" data-testid="cancel-reason-input"
              className="lv-input w-full px-3 py-2 border border-slate-200 rounded-xl text-sm" />
            {cancelError && <div className="mt-2 text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2" data-testid="cancel-error">{cancelError}</div>}
            <div className="flex gap-3 mt-4">
              <button onClick={() => setCancelTarget(null)} disabled={cancelling} data-testid="cancel-modal-keep-btn" className="an-btn flex-1 h-10 px-4 border border-slate-200 rounded-xl text-slate-700 text-sm font-semibold hover:bg-slate-50 disabled:opacity-50">Keep Leave</button>
              <button onClick={handleConfirmCancel} disabled={cancelling} data-testid="cancel-modal-confirm-btn" className="an-btn flex-1 h-10 flex items-center justify-center gap-2 px-4 bg-red-600 text-white rounded-xl text-sm font-semibold hover:bg-red-700 disabled:opacity-50">
                {cancelling ? <><Loader2 className="w-4 h-4 animate-spin" /> {cancelTarget.status === 'approved' ? 'Submitting...' : 'Withdrawing...'}</> : <><Ban className="w-4 h-4" /> {cancelTarget.status === 'approved' ? 'Yes, Request Cancellation' : 'Yes, Withdraw'}</>}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MyLeaves;
